/**
 * Small presentational formatters shared by the four views. Timestamps are ALWAYS rendered in the
 * venue timezone via `@repo/i18n`'s `formatDateTime` (localization-i18n R5) — never device-local.
 */
import { formatDateTime } from "@repo/i18n";
import type { StaffBookingSummaryResponse } from "@repo/api-client";

/** Type guard: the value is a present number (not null/undefined) — the strict-equality helper. */
export function isNum(x: unknown): x is number {
  return typeof x === "number";
}

/** A displayable guest name from the frozen snapshot fields, or a localized "no name" fallback. */
export function guestName(
  b: Pick<StaffBookingSummaryResponse, "guestGivenName" | "guestFamilyName">,
  noNameLabel: string,
): string {
  const name = [b.guestGivenName, b.guestFamilyName].filter(Boolean).join(" ").trim();
  return name || noNameLabel;
}

/** Venue-local date+time for a booking's slot start (epoch seconds). */
export function bookingWhen(startSec: number, timeZone: string, locale: string): string {
  return formatDateTime(startSec * 1000, { timeZone, locale });
}

/** Venue-local `HH:mm` (FR) / `h:mm AM` (EN) time-of-day only. */
export function bookingClock(startSec: number, timeZone: string, locale: string): string {
  const isEnglish = String(locale).toLowerCase().startsWith("en");
  return new Intl.DateTimeFormat(isEnglish ? "en-US" : "fr-FR", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: isEnglish,
  }).format(new Date(startSec * 1000));
}

/**
 * Convert a venue-LOCAL wall clock (`yyyy-MM-dd` + `HH:mm`) to epoch seconds, DST-correct, without a
 * date library (ADR-0013). Manual entry collects the time a staff member reads on the wall; the API
 * wants epoch seconds. Two offset passes settle spring-forward/fall-back boundaries.
 */
export function wallClockToEpochSec(dateStr: string, timeStr: string, timeZone: string): number {
  const dp = dateStr.split("-");
  const tp = timeStr.split(":");
  const y = Number(dp[0]);
  const mo = Number(dp[1]);
  const d = Number(dp[2]);
  const h = Number(tp[0]);
  const mi = Number(tp[1]);
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const offsetMs = (utcMs: number): number => {
    const parts = dtf.formatToParts(new Date(utcMs));
    const get = (t: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === t)?.value);
    const hour = get("hour") === 24 ? 0 : get("hour");
    const asUtc = Date.UTC(
      get("year"),
      get("month") - 1,
      get("day"),
      hour,
      get("minute"),
      get("second"),
    );
    return asUtc - utcMs;
  };
  const naive = Date.UTC(y, mo - 1, d, h, mi);
  const guess = naive - offsetMs(naive);
  const settled = naive - offsetMs(guess);
  return Math.floor(settled / 1000);
}

/** Split an epoch instant into venue-local `date`/`time` input strings (for prefilling a form). */
export function epochToWallInputs(
  startSec: number,
  timeZone: string,
): { date: string; time: string } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(startSec * 1000));
  const get = (t: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === t)?.value ?? "00";
  const hour = get("hour") === "24" ? "00" : get("hour");
  return { date: `${get("year")}-${get("month")}-${get("day")}`, time: `${hour}:${get("minute")}` };
}

/** Today's service day (ISO `yyyy-MM-dd`) in the venue timezone — the default day anchor. */
export function todayServiceDay(timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const get = (t: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}
